
import moment from "moment/moment";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  DropAttivitaApiResponse,
  DropNodiApiResponse,
  DropProgettiApiResponse,
  mapJsonToTabSettings,
  mapTabSettingsToJson,
  TabSettings
} from "./types";
import AxiosInstance from "../components/Axios";
import AxiosInstanceUpload from "../components/AxiosUpload";
import * as Yup from "yup";
import {EmailForm} from "../pages/reportsospversore/types";
import { useState, useEffect, useMemo } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import type {MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState} from 'material-react-table';
import { debounce } from '@mui/material/utils';
import {DipendentiApiResponse, mapJsonToDipendentiApiResponse} from "../pages/gestversore/types";


export function useTabSettings(tabName: string, username: string) {
  const { data: tabSettings } = useGetTabSettings(username, tabName);
  const { mutate: updateSettings } = useUpdateTabSettings();
  const { mutate: createSettings } = useCreateTabSettings();


  const [isTabSetReady, setIsTabSetReady] = useState(false);


  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const saveTabSettings = useMemo(() => debounce(
    (
      newVisibility: VisibilityState,
      newFilters: MRT_ColumnFiltersState,
      newSorting: MRT_SortingState,
      newPagination: MRT_PaginationState
    ) => {
      const payload = {
        username,
        tab: tabName,
        cols: JSON.stringify({
          visibility: newVisibility,
          filters: newFilters,
          sorting: newSorting,
          pagination: newPagination,
        }),
        updated: new Date(),
      };

      if (tabSettings?.id) {
        updateSettings({ ...payload, id: tabSettings.id });
      } else {
        createSettings(payload);
      }
    },
    1000
  ), [tabSettings?.id, username, tabName, updateSettings, createSettings]);

  useEffect(() => {
    if (!tabSettings?.cols) return;

    try {
      const saved = JSON.parse(tabSettings.cols);

      if (saved.visibility) setColumnVisibility(saved.visibility);
      if (saved.filters) {
        const parsedFilters = saved.filters.map((f: any) => {
          if (Array.isArray(f.value) && f.value.length === 2 && Date.parse(f.value[0]) && Date.parse(f.value[1])) {
            return { ...f, value: [new Date(f.value[0]), new Date(f.value[1])] };
          }
          return f;
        });
        setColumnFilters(parsedFilters);
      }
      if (saved.sorting) setSorting(saved.sorting);
      if (saved.pagination) setPagination(saved.pagination);

          setIsTabSetReady(true);

    } catch (e) {

      console.warn('Invalid tab settings JSON:', e);
          setIsTabSetReady(true);
    }
  }, [tabSettings]);

  return {
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
    saveTabSettings,
    isTabSetReady
  };
}



// GET one TabSettings
export function useGetTabSettings(username: string, tab: string) {
  return useQuery<TabSettings>({
    queryKey: ['fetchTabSettings', username, tab],
    queryFn: async () => {
      const response = await AxiosInstance.get(
        `tabsettings/?BT75_USERNAME=${username}&BT75_TAB=${tab}`
      );

      const data = response.data;

      // ✅ THIS IS THE FIX
      if (Array.isArray(data.results) && data.results.length > 0) {
        return mapJsonToTabSettings(data.results[0]);
      }

      return {
        id: 0,
        username,
        tab,
        cols: '',
        updated: new Date(),
      } as TabSettings;
    },
    refetchOnWindowFocus: false,
  });
}

// CREATE TabCol
export function useCreateTabSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tabsettings: TabSettings) => {
     
      const response = await AxiosInstance.post('tabsettings/0/', mapTabSettingsToJson(tabsettings));
      return mapJsonToTabSettings(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchTabSettings'] });
    },
  });
}

// UPDATE TabCol
export function useUpdateTabSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tabsettings: TabSettings) => {
      if (!tabsettings.id) throw new Error('ID is required for update');
      const response = await AxiosInstance.patch('tabsettings/'+tabsettings.id+'/', mapTabSettingsToJson(tabsettings));
      return mapJsonToTabSettings(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchTabSettings'] });
    },
  });
}

// DELETE TabCol
export function useDeleteTabSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
         const response = await AxiosInstance.delete('tabsettings/'+id+'/');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchTabSettings'] });
    },
  });
}




export function columnFiltersParamWithDateRange(  columnFilters: MRT_ColumnFiltersState,  pretab: string = ''  ): string {
  return columnFilters
    .map(filter => {
      // const field = (pretab ? `${pretab}_${filter.id}` : filter.id).toUpperCase();

      const field = (() => {
        const base = pretab ? `${pretab}_${filter.id}` : filter.id;
        const parts = base.split("__");
        if (parts.length === 1) {
          // No __, uppercase whole thing
          return parts[0].toUpperCase();
        } else {
          // Uppercase only the part before __, keep the rest unchanged and join back
          return parts[0].toUpperCase() + "__" + parts.slice(1).join("__");
        }
      })();


       // const field = (pretab ? `${pretab}_${filter.id}` : filter.id);

      // ✅ Handle date-range filters
      if (Array.isArray(filter.value) && filter.value.length === 2) {
        const [start, end] = filter.value;

        const startDate = start ? moment(start).format('YYYY-MM-DD') : '';
        const endDate = end ? moment(end).format('YYYY-MM-DD') : '';

        let query = '';
        if (startDate) query += `&${field}_gte=${encodeURIComponent(startDate)}`;
        if (endDate) query += `&${field}_lte=${encodeURIComponent(endDate)}`;

        return query;
      }

      // ✅ Handle simple values (string | number | boolean | null)
      if (
        filter.value !== undefined &&
        filter.value !== null &&
        (typeof filter.value === 'string' || typeof filter.value === 'number' || typeof filter.value === 'boolean')
      ) {
        return `&${field}=${encodeURIComponent(String(filter.value))}`;
      }

      return '';
    })
    .filter(Boolean)
    .join('');
}

export function columnFiltersParamWithDateRange2(
  columnFilters: MRT_ColumnFiltersState
): string {
  return columnFilters
    .map(filter => {
      const field = filter.id; // keep case unchanged

      // ✅ Handle date-range filters
      if (Array.isArray(filter.value) && filter.value.length === 2) {
        const [start, end] = filter.value;

        const startDate = start ? moment(start).format('YYYY-MM-DD') : '';
        const endDate = end ? moment(end).format('YYYY-MM-DD') : '';

        let query = '';
        if (startDate) query += `&${field}_gte=${encodeURIComponent(startDate)}`;
        if (endDate) query += `&${field}_lte=${encodeURIComponent(endDate)}`;

        return query;
      }

      // ✅ Handle simple values
      if (
        filter.value !== undefined &&
        filter.value !== null &&
        (typeof filter.value === 'string' ||
          typeof filter.value === 'number' ||
          typeof filter.value === 'boolean')
      ) {
        return `&${field}=${encodeURIComponent(String(filter.value))}`;
      }

      return '';
    })
    .filter(Boolean)
    .join('');
}




export function sortingParam2(sorting: MRT_SortingState) {
  let ordering = '';
  for (let i = 0; i < sorting.length; i++) {
    const { id, desc } = sorting[i];
    const field = `${id}`;
    ordering += desc ? `-${field},` : `${field},`;
  }
  // Remove the trailing comma
  ordering = ordering.slice(0, -1);
  return ordering;
}





export function sortingParam(sorting: MRT_SortingState, pretab: string) {
  let ordering = '';
  for (let i = 0; i < sorting.length; i++) {
    const { id, desc } = sorting[i];
    const field = (pretab ? `${pretab}_${id}` : id).toUpperCase();
    ordering += desc ? `-${field},` : `${field},`;
  }
  // Remove the trailing comma
  ordering = ordering.slice(0, -1);
  return ordering;
}



export function useSendEmail() {
  return useMutation({
    mutationFn: async (emailForm: EmailForm) => {
      return AxiosInstance.post('sendemail/', emailForm);
    },
  });
}

export function useUploadReport() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      return AxiosInstanceUpload.post('uploadreport/', formData);
    },
  });
}


export const validationSchema = Yup.object().shape({
                      fromDate: Yup.date().required('Selezione Data Inizio'),
                      toDate: Yup.date().test('is-after-or-equal', 'La data di fine deve essere successiva alla data di inizio', function (value) {
                                         const { fromDate } = this.parent; // Access fromDate from parent object
                                         if (!fromDate || !value) {
                                         return true; // Skip validation if fromDate or toDate is not set
                                      }
                                      return fromDate <= value; // Validate if toDate is after or equal to fromDate
                                    }),
                      emailTo: Yup.string().email('Email non è valida'),
                    });


export const formatDateTimeIT = (value: any) => {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit', // use 'numeric' if you prefer full year
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } else {
    return 'Nessuna Data';
  }
};

export function useGetDropProgettiByCodDip() {
  return useQuery<DropProgettiApiResponse>({
    queryKey: ['fetchDropProgettiByCodDip'],
    queryFn: async () => {

       let keycoddip = localStorage.getItem('keycoddip') || '';
       const keyrole = localStorage.getItem('keyrole') || '';

       const isAdmin =  keyrole.includes('TimeReportAdmin') ;

       let myURL = '';


         if (!isAdmin) {
                  myURL = `dropprogettibydip/?limit=10000&offset=0&coddip__exact=${keycoddip}`;
                }
         else
         {
                  myURL = 'dropprogetti/?limit=10000&offset=0';
         }

      const response = await AxiosInstance.get(`${myURL}`);
      return response.data
    },
    refetchOnWindowFocus: false,
  });
}

export function useGetDropProgettiByRef() {
  return useQuery<DropProgettiApiResponse>({
    queryKey: ['fetchDropProgettiByDipRef'],
    queryFn: async () => {

       let keycoddip = localStorage.getItem('keycoddip') || '';
       const keyrole = localStorage.getItem('keyrole') || '';

       const isAdmin = keyrole.includes("TimeReportAdmin") || keycoddip.trim().toUpperCase() === "DS";

       let myURL = '';

         if (!isAdmin) {
                  myURL = `dropprogetti/?limit=10000&offset=0&ditta=2&ref=${keycoddip}`;
                }
         else
         {
                  myURL = 'dropprogetti/?limit=10000&offset=0&ditta=2';
         }

      const response = await AxiosInstance.get(`${myURL}`);
      return response.data
    },
    refetchOnWindowFocus: false,
  });
}



export function useGetDropNodiByIdProg(idprog: string) {
  return useQuery<DropNodiApiResponse>({
    queryKey: ['fetchDropNodiByIdProg', idprog],
    queryFn: async () => {
      const response = await AxiosInstance.get('dropnodi/?limit=10000&offset=0&idprog__exact=' + idprog);
      return response.data
    },
    enabled: !!idprog,
    refetchOnWindowFocus: false,
  });
}


export function useGetDropAttivitaByIdProg(idprog: string) {
  return useQuery<DropAttivitaApiResponse>({
    queryKey: ['fetchDropAttivitaByIdProg', idprog],
    queryFn: async () => {
      const response = await AxiosInstance.get('dropattivita/?limit=10000&offset=0&idprog__exact=' + idprog);
      return response.data
    },
    enabled: !!idprog,
    refetchOnWindowFocus: false,
  });
}

export function useGetDipendentiProg(ditta: number, coddip: string) {
  return useQuery<DipendentiApiResponse>({
    queryKey: ['fetchDipendenti', ditta, coddip],
    queryFn: async () => {
      let url = 'dipendenti/?limit=10000&offset=0&VBT03_FLGPROG=Y';

      if (ditta !== 0) {
        url += `&VBT03_DITTA=${ditta}`;
        if (coddip) {
          url += `&VBT03_CODDIP=${coddip}`;
        }
      }

      const response = await AxiosInstance.get(url);
      return mapJsonToDipendentiApiResponse(response.data);
    },
    refetchOnWindowFocus: false,
  });
}

